import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { localizeValue } from "../lib/displayValues";
import { Gavel, Clock, TrendingUp, ArrowRight, Flame } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Compact live-auctions strip for the homepage — shows top-3 auctions
// closest to ending, with live countdowns. Designed to create urgency
// (FOMO) and drive traffic to /auctions.
// ─────────────────────────────────────────────────────────────────────
export default function LiveAuctionsStrip() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/auctions/live", { params: { limit: 3 } })
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a0a0a 50%,#3a0a0a 100%)" }}
      data-testid="home-live-auctions-strip"
    >
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <div className="absolute top-0 start-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#dc2626" }} />
        <div className="absolute bottom-0 end-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#c9a55c" }} />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest mb-3" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              {lang === "ar" ? "مزادات مباشرة" : "Live auctions"}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] font-semibold leading-[1.05]" style={{ color: "#f7eecd" }}>
              {lang === "ar"
                ? <>مزادات تنتهي <span style={{ color: "#e8c873" }}>قريباً</span></>
                : <>Ending <span style={{ color: "#e8c873" }}>soon</span></>}
            </h2>
            <p className="mt-2 text-sm sm:text-base font-medium max-w-xl" style={{ color: "rgba(247,238,205,0.75)" }}>
              {lang === "ar"
                ? "أعلى مزايدة تفوز. لا تفوّت الفرصة — قدّم عرضك الآن قبل انتهاء الوقت."
                : "Highest bid wins. Don't miss out — place your bid before time runs out."}
            </p>
          </div>
          <Link
            to="/auctions"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold shadow-xl transition-transform hover:scale-105"
            style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}
            data-testid="home-auctions-view-all"
          >
            {lang === "ar" ? "شاهد كل المزادات" : "See all auctions"}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-3 gap-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`skl-${i}`} className="h-80 rounded-3xl animate-pulse" style={{ background: "rgba(255,255,255,0.05)" }} />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-5" data-testid="home-auctions-grid">
            {items.map((v) => <StripCard key={v.id} v={v} lang={lang} />)}
          </div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
function StripCard({ v, lang }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = useMemo(() => {
    if (!v.auction_ends_at) return null;
    const diff = new Date(v.auction_ends_at).getTime() - now;
    if (diff <= 0) return { ended: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
      ended: false,
    };
  }, [now, v.auction_ends_at]);

  const tr = (s) => localizeValue(s, lang);
  const current = Number(v.current_bid_sar || 0);
  const starting = Number(v.starting_bid_sar || v.price_sar || 0);
  const bidCount = Number(v.bid_count || 0);
  const endingSoon = time && !time.ended && time.days === 0 && time.hours < 12;

  return (
    <Link
      to={`/vehicle/${v.id}`}
      className="group block rounded-3xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-2xl"
      style={{ background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(220,38,38,0.35)" }}
      data-testid={`home-auction-card-${v.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={v.image || v.images?.[0]} alt={`${v.make} ${v.model}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 start-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          {lang === "ar" ? "مباشر" : "Live"}
        </div>
        {time && !time.ended && (
          <div className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold shadow-md tabular-nums" style={endingSoon
            ? { background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#0a0a0a", border: "1px solid #f59e0b" }
            : { background: "rgba(10,10,10,0.85)", color: "#e8c873", border: "1px solid #c9a55c" }}>
            <Clock className="w-3 h-3" />
            {time.days > 0 && <>{time.days}{lang === "ar" ? "ي" : "d"} </>}
            {String(time.hours).padStart(2, "0")}:{String(time.mins).padStart(2, "0")}:{String(time.secs).padStart(2, "0")}
          </div>
        )}
        {bidCount > 0 && (
          <div className="absolute bottom-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold bg-white/95 text-[#0a0a0a]">
            <TrendingUp className="w-3 h-3 text-[#dc2626]" />
            {bidCount} {lang === "ar" ? (bidCount === 1 ? "مزايدة" : "مزايدات") : (bidCount === 1 ? "bid" : "bids")}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "rgba(247,238,205,0.6)" }}>
          {v.year} · {v.make} · {tr(v.city)}
        </div>
        <div className="font-display text-xl font-bold mt-1 truncate group-hover:text-[#fca5a5] transition-colors" style={{ color: "#f7eecd" }}>
          {v.model}
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#fca5a5]">
              {current > 0 ? (lang === "ar" ? "أعلى مزايدة" : "Current bid") : (lang === "ar" ? "سعر البدء" : "Starting")}
            </div>
            <div className="font-display text-2xl font-bold" style={{ color: "#f7eecd" }}>
              {formatSAR(current || starting)} <span className="text-xs font-medium" style={{ color: "rgba(247,238,205,0.65)" }}>SAR</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-transform group-hover:scale-105" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
            <Flame className="w-3 h-3" />
            {lang === "ar" ? "زايد" : "Bid"}
          </div>
        </div>
      </div>
    </Link>
  );
}
